import React, {
  useCallback, useState, useMemo, useEffect, useRef,
} from 'react';
import {
  makeStyles, shorthands, tokens, mergeClasses, DialogProps, Tooltip,
} from '@fluentui/react-components';
import { I18nModel } from '@bingads-webui/copilot-i18n';
import { Info16Regular } from '@fluentui/react-icons';
import { Textarea, TextareaProps } from '@fluentai/textarea';
import { ITextSuggestionListProps, TextSuggestionList } from './TextSuggestionList';
import { InlineCopilotDialog } from './InlineCopilotDialog';
import { ErrorMessageBar } from './ErrorMessageBar';
import { ISubCopilotProps } from './types.d';
import { InlineCopilotDescription } from './Description';
import { Loading } from './Loading';
import {
  LOADING_TEXT, NO_OPTIONS_TEXT, REPLACE_BTN_TEXT, REVISE_DESCRIPTION, REVISE_DIALOG_TOOLTIP, REVISE_TEXTAREA_HINT, REVISE_TEXTAREA_PLACEHOLDER, UPDATED_RECOMMENDATION_TEXT,
} from './staticTexts';

interface IRewriteLoadingProps {
  i18n: I18nModel;
}

const useRewriteLoadingStyles = makeStyles({
  root: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: tokens.spacingVerticalL,
    ...shorthands.overflow('hidden'),
  },
});

const RewriteLoading: React.FC<IRewriteLoadingProps> = React.memo(({ i18n }) => {
  const styles = useRewriteLoadingStyles();

  return (
    <div className={mergeClasses(styles.root, 'rewrite-loading')}>
      <Loading loadingText={i18n.getString(LOADING_TEXT)} />
    </div>
  );
});

const useRewriteStyles = makeStyles({
  root: {
    overflowX: 'hidden',
  },
  hint: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightBold,
  },
  result: {
    marginTop: tokens.spacingVerticalL,
  },
  errorMessage: {
    marginTop: tokens.spacingVerticalL,
  },
  description: {
    marginTop: tokens.spacingVerticalL,
  },
  tooltipTrigger: {
    marginLeft: tokens.spacingHorizontalXS,
    height: '16px',
  },
  textArea: {
    '& .fai-Textarea .fai-Textarea__count': {
      position: 'absolute',
      bottom: tokens.spacingHorizontalXL,
      left: tokens.spacingHorizontalXL,
    },
  },
  errorTip: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
});

export const Rewrite: React.FC<ISubCopilotProps> = React.memo((props) => {
  const styles = useRewriteStyles();
  const {
    i18n,
    open,
    onOpenChange,
    onRequestAsset,
    assetType,
    assets,
    error,
    loading,
    onSelect,
    title,
    closeButtonLabel,
    onClearAsset,
    modalType,
    isNewLogo,
  } = props;
  const [userInput, setUserInput] = useState<string>('');
  const [showCount, setShowCount] = useState(true);

  useEffect(() => {
    if (assets) {
      // new suggestions have been received, clear user input
      setUserInput('');

      // BUG: @fluentai/textarea will display an incorrect count after setting the input to empty string, hide the count until user types again
      setShowCount(false);
    }
  }, [assets]);

  const onInputChange = useCallback<NonNullable<TextareaProps['onChange']>>((_ev, { value: v }) => {
    setUserInput(v);
    setShowCount(true);
  }, []);

  const onSubmit = useCallback<NonNullable<TextareaProps['onSubmit']>>(() => {
    if (userInput.trim().length > 0) {
      onClearAsset();
      onRequestAsset({
        requestType: 'rewrite',
        value: userInput,
      });
    }
  }, [onClearAsset, onRequestAsset, userInput]);

  const sendButtonProps = useMemo(() => {
    const disabled = loading || !userInput || userInput.trim().length === 0;
    return { disabled };
  }, [loading, userInput]);

  const onSelectCallback = useCallback<ITextSuggestionListProps['onSelect']>(({ value }) => {
    onSelect({ value: [{ text: value }] });
  }, [onSelect]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current?.focus();
    }
  }, [loading, open, userInput]);

  const handleKeyUp = useCallback((evt: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (evt.key === 'Enter' && !evt.shiftKey) {
      // Enter was pressed, interpret this as a submit action
      onSubmit(null as any, { value: userInput });
    }
  }, [onSubmit, userInput]);

  const handleKeyDown = useCallback((evt: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (evt.key === 'Enter' && !evt.shiftKey) {
      // only Shift+Enter can insert a new line
      evt.preventDefault();
    }
  }, []);

  const handleOpenChange = useCallback<NonNullable<DialogProps['onOpenChange']>>((ev, data) => {
    const { open: o } = data;
    if (!o) {
      setUserInput('');
    }

    onOpenChange?.(ev, data);
  }, [onOpenChange]);

  return (
    <InlineCopilotDialog
      title={title}
      open={open}
      onOpenChange={handleOpenChange}
      closeButtonLabel={closeButtonLabel}
      modalType={modalType}
      isNewLogo={isNewLogo}
    >
      <div className="rewrite-dialog">
        <div className={styles.root}>
          <div className={styles.hint}>
            {i18n.getString(REVISE_TEXTAREA_HINT[assetType])}
            <Tooltip content={i18n.getString(REVISE_DIALOG_TOOLTIP)} relationship="description">
              <div className={styles.tooltipTrigger}><Info16Regular /></div>
            </Tooltip>
          </div>
          <div className={styles.textArea}>
            <Textarea
              ref={textareaRef}
              value={userInput}
              disabled={loading}
              send={sendButtonProps}
              placeholder={i18n.getString(REVISE_TEXTAREA_PLACEHOLDER)}
              onChange={onInputChange}
              onSubmit={onSubmit}
              onKeyDown={handleKeyDown}
              onKeyUp={handleKeyUp}
              maxLength={300}
              showCount={showCount}
            />
          </div>
          <InlineCopilotDescription className={styles.description}>
            {i18n.getString(REVISE_DESCRIPTION)}
          </InlineCopilotDescription>
        </div>
        {assets && assets.length ? (
          <div className={styles.result}>
            <TextSuggestionList
              description={i18n.getString(UPDATED_RECOMMENDATION_TEXT)}
              suggestions={assets}
              onSelect={onSelectCallback}
              buttonText={i18n.getString(REPLACE_BTN_TEXT)}
            />
          </div>
        ) : null}
        {assets && assets.length === 0 && !error && (
          <h3 className={styles.errorTip}>{i18n.getString(NO_OPTIONS_TEXT)}</h3>
        )}
        {error && <ErrorMessageBar className={styles.errorMessage}>{error}</ErrorMessageBar>}
        {loading ? <RewriteLoading i18n={i18n} /> : null}
      </div>
    </InlineCopilotDialog>
  );
});
